import React, { createContext, useContext, useEffect, useState } from 'react';
import { portfolioAPI } from '@/services/api';
import { portfolioData as mockData } from '@/data/mock';
import { toast } from 'react-toastify';

const PortfolioContext = createContext({
  portfolioData: mockData,
  loading: true,
  error: null,
  refetch: () => {},
  updateHero: () => {},
  updateAbout: () => {},
  updateProjects: () => {},
  updateSkills: () => {},
  updateExperience: () => {},
  addProject: () => {},
  updateProject: () => {},
  deleteProject: () => {},
  addSkill: () => {},
  updateSkill: () => {},
  deleteSkill: () => {},
  addExperience: () => {},
  updateExperience: () => {},
  deleteExperience: () => {},
  addProblemSolved: () => {},
  updateProblemsSolved: () => {},
  deleteProblemSolved: () => {},
  updateDevOpsShowcase: () => {},
  updateChatbotResponses: () => {},
});

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within PortfolioProvider');
  }
  return context;
};

export const PortfolioProvider = ({ children }) => {
  const [data, setData] = useState(mockData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch portfolio data from API
  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await portfolioAPI.getPortfolioData();
      if (response.success) {
        setData(response.data);
        localStorage.setItem('portfolio-data', JSON.stringify(response.data));
      } else {
        toast.error(response.message || 'Erreur lors du chargement des données');
      }
    } catch (err) {
      // console.error('Error fetching portfolio data:', err);
      toast.error(err.message || 'Erreur lors du chargement des données');
      setError(err.message || 'Erreur lors du chargement des données');
      
      const savedData = localStorage.getItem('portfolio-data');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          setData(parsedData);
          toast.info('Données chargées depuis le cache');
        } catch (parseError) {
          // console.error('Error parsing saved data:', parseError);
          toast.error('Erreur lors du chargement des données depuis le cache');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const updateHero = async (heroData) => {
    try {
      const response = await portfolioAPI.updateHero(heroData);
      if (response.success) {
        setData(prev => {
          const updatedData = { ...prev, hero: response.data };
          localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
          return updatedData;
        });
        toast.success(response.message || 'Hero mis à jour avec succès');
        return { success: true, message: response.message };
      } else {
        toast.error(response.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      // console.error('Error updating hero:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour');
      return { success: false, message: error.message };
    }
  };

  const updateAbout = async (aboutData) => {
    try {
      const response = await portfolioAPI.updateAbout(aboutData);
      if (response.success) {
        setData(prev => {
          const updatedData = { ...prev, about: response.data };
          localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
          return updatedData;
        });
        toast.success(response.message || 'About mis à jour avec succès');
        return { success: true, message: response.message };
      } else {
        toast.error(response.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      // console.error('Error updating about:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour');
      return { success: false, message: error.message };
    }
  };

  const updateProjects = (projects) => {
    setData(prev => ({
      ...prev,
      projects
    }));
  };

  const updateSkills = (skills) => {
    setData(prev => ({
      ...prev,
      skills
    }));
  };

  const updateExperience = (experience) => {
    setData(prev => ({
      ...prev,
      experience
    }));
  };

  const addProject = async (project) => {
    try {
      const response = await portfolioAPI.createProject(project);
      if (response.success) {
        setData(prev => {
          const updatedData = { ...prev, projects: [...prev.projects, response.data] };
          localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
          return updatedData;
        });
        toast.success(response.message || 'Projet ajouté avec succès'); 
        return { success: true, message: response.message, data: response.data };
      } else {
        toast.error(response.message || 'Erreur lors de la création');
      }
    } catch (error) {
      // console.error('Error adding project:', error);
      toast.error(error.message || 'Erreur lors de la création');
      return { success: false, message: error.message };
    }
  };

  const updateProject = async (projectId, projectData) => {
    try {
      const response = await portfolioAPI.updateProject(projectId, projectData);
      if (response.success) {
        setData(prev => {
          const updatedData = {
            ...prev,
            projects: prev.projects.map(project =>
              project.id === projectId ? response.data : project
            )
          };
          localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
          return updatedData;
        });
        toast.success(response.message || 'Projet mis à jour avec succès');
        return { success: true, message: response.message, data: response.data };
      } else {
        toast.error(response.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      // console.error('Error updating project:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour');
      return { success: false, message: error.message };
    }
  };

  const deleteProject = async (projectId) => {
    try {
      const response = await portfolioAPI.deleteProject(projectId);
      if (response.success) {
        setData(prev => {
          const updatedData = {
            ...prev,
            projects: prev.projects.filter(project => project.id !== projectId)
          };
          localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
          return updatedData;
        });
        toast.success(response.message || 'Projet supprimé avec succès');
        return { success: true, message: response.message };
      } else {
        toast.error(response.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      // console.error('Error deleting project:', error);
      toast.error(error.message || 'Erreur lors de la suppression');
      return { success: false, message: error.message };
    }
  };

  const addSkill = async (category, skillData) => {
    try {
      const response = await portfolioAPI.createSkill({ ...skillData, category });
      if (response.success) {
        const skillsResponse = await portfolioAPI.getSkills();
        if (skillsResponse.success) {
          setData(prev => {
            const updatedData = { ...prev, skills: skillsResponse.data };
            localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
            return updatedData;
          });
        }
        toast.success(response.message || 'Compétence ajoutée avec succès');
        return { success: true, message: response.message, data: response.data };
      } else {
        toast.error(response.message || 'Erreur lors de la création');
      }
    } catch (error) {
      // console.error('Error adding skill:', error);
      toast.error(error.message || 'Erreur lors de la création');
      return { success: false, message: error.message };
    }
  };

  const updateSkill = async (category, skillIndex, skillData) => {
    try {
      const skillToUpdate = data.skills[category]?.[skillIndex];
      if (!skillToUpdate || !skillToUpdate._id) {
        toast.error('Compétence introuvable');
        return { success: false, message: 'Compétence introuvable' };
      }
      
      const response = await portfolioAPI.updateSkill(skillToUpdate._id, skillData);
      if (response.success) {
        const skillsResponse = await portfolioAPI.getSkills();
        if (skillsResponse.success) {
          setData(prev => {
            const updatedData = { ...prev, skills: skillsResponse.data };
            localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
            return updatedData;
          });
        }
        toast.success(response.message || 'Compétence mise à jour avec succès');
        return { success: true, message: response.message, data: response.data };
      } else {
        toast.error(response.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      // console.error('Error updating skill:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour');
      return { success: false, message: error.message };
    }
  };

  const deleteSkill = async (category, skillIndex) => {
    try {
      const skillToDelete = data.skills[category]?.[skillIndex];
      if (!skillToDelete || !skillToDelete._id) {
        toast.error('Compétence introuvable');
        return { success: false, message: 'Compétence introuvable' };
      }
      
      const response = await portfolioAPI.deleteSkill(skillToDelete._id);
      if (response.success) {
        const skillsResponse = await portfolioAPI.getSkills();
        if (skillsResponse.success) {
          setData(prev => {
            const updatedData = { ...prev, skills: skillsResponse.data };
            localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
            return updatedData;
          });
        }
        toast.success(response.message || 'Compétence supprimée avec succès');
        return { success: true, message: response.message };
      } else {
        toast.error(response.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      // console.error('Error deleting skill:', error);
      toast.error(error.message || 'Erreur lors de la suppression');
      return { success: false, message: error.message };
    }
  };

  const addExperienceItem = async (experience) => {
    try {
      const response = await portfolioAPI.createExperience(experience);
      if (response.success) {
        setData(prev => {
          const updatedData = { ...prev, experience: [...prev.experience, response.data] };
          localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
          return updatedData;
        });
        toast.success(response.message || 'Expérience ajoutée avec succès');
        return { success: true, message: response.message, data: response.data };
      } else {
        toast.error(response.message || 'Erreur lors de la création');
      }
    } catch (error) {
      // console.error('Error adding experience:', error);
      toast.error(error.message || 'Erreur lors de la création');
      return { success: false, message: error.message };
    }
  };

  const updateExperienceItem = async (experienceId, experienceData) => {
    try {
      const response = await portfolioAPI.updateExperience(experienceId, experienceData);
      if (response.success) {
        setData(prev => {
          const updatedData = {
            ...prev,
            experience: prev.experience.map(exp =>
              exp.id === experienceId ? response.data : exp
            )
          };
          localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
          return updatedData;
        });
        toast.success(response.message || 'Expérience mise à jour avec succès');
        return { success: true, message: response.message, data: response.data };
      } else {
        toast.error(response.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      // console.error('Error updating experience:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour');
      return { success: false, message: error.message };
    }
  };

  const deleteExperienceItem = async (experienceId) => {
    try {
      const response = await portfolioAPI.deleteExperience(experienceId);
      if (response.success) {
        setData(prev => {
          const updatedData = {
            ...prev,
            experience: prev.experience.filter(exp => exp.id !== experienceId)
          };
          localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
          return updatedData;
        });
        toast.success(response.message || 'Expérience supprimée avec succès');
        return { success: true, message: response.message };
      } else {
        toast.error(response.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      // console.error('Error deleting experience:', error);
      toast.error(error.message || 'Erreur lors de la suppression');
      return { success: false, message: error.message };
    }
  };

  const addProblemSolved = async (problemSolved) => {
    try {
      const response = await portfolioAPI.createProblemSolved(problemSolved);
      if (response.success) {
        setData(prev => {
          const updatedData = {
            ...prev,
            problemsSolved: [...(prev.problemsSolved || []), response.data],
          };
          localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
          return updatedData;
        });
        toast.success(response.message || 'Problème résolu ajouté avec succès');
        return { success: true, message: response.message, data: response.data };
      } else {
        toast.error(response.message || 'Erreur lors de l\'ajout');
        return { success: false, message: response.message };
      }
    } catch (error) {
      // console.error('Error adding problem solved:', error);
      toast.error(error.message || 'Erreur lors de l\'ajout des problèmes résolus');
      return { success: false, message: error.message };
    }
  };

  const updateProblemsSolved = async (problemsSolved) => {
    try {
      const results = await Promise.all(
        problemsSolved.map(async (problem) => {
          const id = problem.id ?? problem._id;
          if (!id) {
            return { success: true, data: problem };
          }
          return portfolioAPI.updateProblemSolved(id, problem);
        })
      );

      const failed = results.find((result) => result && result.success === false);
      if (failed) {
        toast.error(failed.message || 'Erreur lors de la mise à jour des problèmes résolus');
        return { success: false, message: failed.message };
      }

      const updatedProblems = results.map((result, index) => result?.data ?? problemsSolved[index]);

      localStorage.setItem('problems-solved', JSON.stringify(updatedProblems));
      setData((prev) => {
        const updatedData = { ...prev, problemsSolved: updatedProblems };
        localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
        return updatedData;
      });
      toast.success('Problèmes résolus mis à jour avec succès');
      return { success: true, message: 'Problèmes résolus mis à jour avec succès' };
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la mise à jour des problèmes résolus');
      return { success: false, message: error.message };
    }
  };

  const deleteProblemSolved = async (problemSolvedId) => {
    try {
      const response = await portfolioAPI.deleteProblemSolved(problemSolvedId);
      if (response.success) {
        setData((prev) => {
          const updatedData = {
            ...prev,
            problemsSolved: (prev.problemsSolved || []).filter(
              (problem) => (problem.id ?? problem._id) !== problemSolvedId
            ),
          };
          localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
          return updatedData;
        });
        toast.success(response.message || 'Problème résolu supprimé avec succès');
        return { success: true, message: response.message };
      }
      toast.error(response.message || 'Erreur lors de la suppression');
      return { success: false, message: response.message };
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la suppression du problème résolu');
      return { success: false, message: error.message };
    }
  };

  const updateDevOpsShowcase = async (devOpsShowcase) => {
    try {
      // Save to localStorage as this might not have a dedicated API endpoint yet
      localStorage.setItem('devops-showcase', JSON.stringify(devOpsShowcase));
      setData(prev => {
        const updatedData = { ...prev, devOpsShowcase };
        localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
        return updatedData;
      });
      toast.success('DevOps showcase mis à jour avec succès');
      return { success: true, message: 'DevOps showcase mis à jour avec succès' };
    } catch (error) {
      // console.error('Error updating DevOps showcase:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour du DevOps showcase');
      return { success: false, message: error.message };
    }
  };

  const updateChatbotResponses = async (chatbotResponses) => {
    try {
      // Save to localStorage as this might not have a dedicated API endpoint yet
      localStorage.setItem('chatbot-responses', JSON.stringify(chatbotResponses));
      setData(prev => {
        const updatedData = { ...prev, chatbotResponses };
        localStorage.setItem('portfolio-data', JSON.stringify(updatedData));
        return updatedData;
      });
      toast.success('Réponses du chatbot mises à jour avec succès');
      return { success: true, message: 'Réponses du chatbot mises à jour avec succès' };
    } catch (error) {
      // console.error('Error updating chatbot responses:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour des réponses du chatbot');
      return { success: false, message: error.message };
    }
  };

  return (
    <PortfolioContext.Provider
      value={{
        portfolioData: data,
        loading,
        error,
        refetch: fetchPortfolioData,
        updateHero,
        updateAbout,
        updateProjects,
        updateSkills,
        updateExperience,
        addProject,
        updateProject,
        deleteProject,
        addSkill,
        updateSkill,
        deleteSkill,
        addExperience: addExperienceItem,
        updateExperience: updateExperienceItem,
        deleteExperience: deleteExperienceItem,
        addProblemSolved,
        deleteProblemSolved,
        updateProblemsSolved,
        updateDevOpsShowcase,
        updateChatbotResponses,
      }}
    >
      {children}
    </PortfolioContext.Provider>
  );
};
